<script setup>
const props = defineProps({
  links: {
    type: Array,
    default: () => [],
  },
  title: {
    type: String,
    default: 'Rekomendasi Produk',
  },
  subtitle: {
    type: String,
    default: '',
  },
  // Where this block sits: 'home', 'mid', or 'end'. Sent to GTM/GA4 with each click.
  position: {
    type: String,
    default: '',
  },
})

function pushToDataLayer(payload) {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(payload)
}

// Push a click event to the GTM dataLayer. GTM forwards it to GA4.
function trackClick(link, index) {
  const { name, price, url } = parseShopeeText(link.shopee_text)
  pushToDataLayer({
    event: 'affiliate_click',
    affiliate_position: props.position || 'unknown',
    affiliate_item_name: name,
    affiliate_item_price: price,
    affiliate_item_index: index + 1,
    affiliate_url: url,
  })
}

// Fire affiliate_view once, when at least half of the block is on screen.
// Gives GA4 an impression count so CTR = affiliate_click / affiliate_view.
const root = ref(null)
let observer = null
let viewed = false

watch(root, (el) => {
  if (!el || viewed || typeof IntersectionObserver === 'undefined') return
  observer = new IntersectionObserver(
    (entries) => {
      if (viewed || !entries.some((e) => e.isIntersecting)) return
      viewed = true
      pushToDataLayer({
        event: 'affiliate_view',
        affiliate_position: props.position || 'unknown',
        affiliate_item_count: validLinks.value.length,
      })
      observer?.disconnect()
    },
    { threshold: 0.5 }
  )
  observer.observe(el)
})

onBeforeUnmount(() => observer?.disconnect())

function parseShopeeText(text) {
  if (!text) return { name: '', price: '', url: '' }
  const nameMatch = text.match(/Cek (.+?) dengan harga/)
  const priceMatch = text.match(/dengan harga (Rp[\d.,]+)/)
  const urlMatch = text.match(/https?:\/\/\S+/)
  return {
    name: nameMatch?.[1]?.trim() ?? text.substring(0, 80),
    price: (priceMatch?.[1] ?? '').replace(/[.,]+$/, ''),
    url: urlMatch?.[0] ?? '#',
  }
}

const validLinks = computed(() =>
  (props.links || []).filter((l) => l?.shopee_text?.trim())
)
</script>

<template>
  <div v-if="validLinks.length" ref="root" class="my-6 rounded-xl border border-orange-200 bg-orange-50 p-4">
    <div class="mb-3 flex items-center gap-1.5">
      <span class="text-xs font-semibold uppercase tracking-wide text-orange-400">{{ title }}</span>
    </div>
    <p v-if="subtitle" class="mb-3 text-sm text-gray-600">{{ subtitle }}</p>
    <div
      :class="[
        'grid gap-3',
        validLinks.length === 1 ? 'grid-cols-1 md:grid-cols-3' : validLinks.length === 2 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-3',
      ]"
    >
      <a
        v-for="(link, i) in validLinks"
        :key="i"
        :href="parseShopeeText(link.shopee_text).url"
        target="_blank"
        rel="noopener noreferrer sponsored nofollow"
        class="flex flex-col overflow-hidden rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow duration-200"
        @click="trackClick(link, i)"
        @auxclick="trackClick(link, i)"
      >
        <div class="aspect-square w-full overflow-hidden bg-gray-100">
          <img
            v-if="link.image_url || link.image_url_manual"
            :src="link.image_url || link.image_url_manual"
            :alt="parseShopeeText(link.shopee_text).name"
            class="h-full w-full object-cover"
          />
          <div v-else class="flex h-full w-full items-center justify-center">
            <svg class="h-10 w-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        </div>
        <div class="flex flex-1 flex-col p-2.5">
          <p class="mb-1 line-clamp-2 text-xs font-medium text-gray-700 leading-tight">
            {{ parseShopeeText(link.shopee_text).name }}
          </p>
          <p v-if="parseShopeeText(link.shopee_text).price" class="mt-auto text-sm font-bold text-orange-500">
            {{ parseShopeeText(link.shopee_text).price }}
          </p>
          <div class="mt-2 rounded-md bg-orange-500 py-1.5 text-center text-xs font-semibold text-white">
            Beli di Shopee
          </div>
        </div>
      </a>
    </div>
    <p class="mt-3 text-[11px] leading-snug text-gray-400">Link afiliasi: kami mendapat komisi kecil tanpa biaya tambahan untukmu.</p>
  </div>
</template>
